import { TenantRelationAssignment, TenantRelationAssignmentDto } from '@shared/models/tenant-relation-assignment';
import { BookingDialogBaseEntity } from '@sharedComponents/dialogs/booking-dialog/services/classes/abstract/booking-dialog-base-entity';
import { BookingDialogService } from '@sharedComponents/dialogs/booking-dialog/services/booking-dialog.service';
import { BcmBooking } from '@shared/models/bcm-booking';
import { BookingAttribute } from '@sharedComponents/dialogs/booking-dialog/enums/booking-attribute.enum';
import { getVestingPeriodUntil } from '@shared/functions/vesting-period-helper';
import { ProductSubscriptionType } from '@shared/models/product-subscription';

export class BookingDialogTenantRelationAssignment extends BookingDialogBaseEntity<TenantRelationAssignment, TenantRelationAssignment> {

    constructor(bookingDialogService: BookingDialogService) {
        super(bookingDialogService, BookingAttribute.TENANT_RELATION_ASSIGNMENT);

        // Initialize with null dates - they will be set when user selects a tenant relation
        // or clicks the "Datum und Uhrzeit aus Belegung übernehmen" button
        this.value = new TenantRelationAssignment({
            id: 0,
            fromDate: null,
            toDate: null,
            periodFromDate: null,
            periodToDate: null,
            vestingPeriodFrom: null,
            vestingPeriodUntil: null,
            captureTenantRelation: false,
        } as unknown as TenantRelationAssignmentDto);

    }

    protected afterValueSet(): void {

        const tenantRelationChanged = this.bookingDialogService.booking?.tenantRelationAssignment?.tenantRelation?.id !== this.value.tenantRelation?.id;

        // If dates are not set and we have a tenant relation, initialize dates from booking
        if (this.value.tenantRelation?.id && !this.value.fromDate && this.bookingDialogService.booking?.from) {
            const assignmentWithDates = this.getTenantRelationAssignmentWithUpdatedDates();
            this.value.fromDate = assignmentWithDates.fromDate;
            this.value.toDate = assignmentWithDates.toDate;
            this.value.periodFromDate = assignmentWithDates.periodFromDate;
            this.value.periodToDate = assignmentWithDates.periodToDate;
            this.value.vestingPeriodFrom = assignmentWithDates.vestingPeriodFrom;
            this.value.vestingPeriodUntil = assignmentWithDates.vestingPeriodUntil;
        }

        if (!tenantRelationChanged) {
            this.bookingDialogService.subscriptions?.value
                .filter(subscription => subscription.type === ProductSubscriptionType.TenantRelation)
                .map(subscription => subscription.deleted = false);
        }

        if (tenantRelationChanged && this.value.captureTenantRelation && this.value.tenantRelation?.id && this.value.tenantRelation.products?.length) {
            this.value.payableOption = this.value.tenantRelation.payableOption;

            this._resetTenantRelationCharges();

            if (this.value.toDate) {
                this.bookingDialogService.positions.addFromTenantRelation(this.value);
            } else {
                this.bookingDialogService.subscriptions.addFromTenantRelation(this.value);
            }

        } else if ((!tenantRelationChanged || this.value.captureTenantRelation === false) && this.bookingDialogService.initialized) {
            this.bookingDialogService.positions.removeUnsavedFromTenantRelation();
            this.bookingDialogService.subscriptions.removeUnsavedFromTenantRelation();
        }

    }

    /**
     * Räumt die aus der Beziehung erzeugten Leistungen vollständig ab, bevor sie neu angelegt werden.
     *
     * removeFromTenantRelation() allein reicht nicht: es markiert nur Zeilen mit wahrheitswertiger
     * `tenantRelationAssignment.id` als gelöscht, und bei einer Neuanlage ist die id 0. Die alte
     * Position blieb dadurch stehen und blockierte über isDuplicate() das Anlegen der neuen – der
     * Reiter "Leistungen" zeigte weiter den Preis der zuvor gewählten Beziehung, während die
     * "Produkte gefunden"-Box schon die neue auswertete. (#6922)
     *
     * Beide Träger werden geräumt, nicht nur der gerade zuständige: ohne Enddatum entstehen Abos,
     * mit Enddatum Positionen – wer das Enddatum nachträgt, hätte sonst beides in der Buchung.
     */
    private _resetTenantRelationCharges(): void {
        this.bookingDialogService.positions.removeFromTenantRelation();
        this.bookingDialogService.positions.removeUnsavedFromTenantRelation();
        this.bookingDialogService.subscriptions.removeFromTenantRelation();
        this.bookingDialogService.subscriptions.removeUnsavedFromTenantRelation();
    }

    async setBooking(booking: BcmBooking) {
        if (booking.id && booking.tenantRelationAssignment?.id) {
            super.setBooking(booking);
        }
    }

    updateDates() {

        const assignmentWithUpdatedDates = this.getTenantRelationAssignmentWithUpdatedDates();

        this.value.fromDate = assignmentWithUpdatedDates.fromDate;
        this.value.toDate = assignmentWithUpdatedDates.toDate;
        this.value.periodFromDate = assignmentWithUpdatedDates.periodFromDate;
        this.value.periodToDate = assignmentWithUpdatedDates.periodToDate;
        this.value.vestingPeriodFrom = assignmentWithUpdatedDates.vestingPeriodFrom;
        this.value.vestingPeriodUntil = assignmentWithUpdatedDates.vestingPeriodUntil;

        if (this.value.toDate) {
            const sameFromDate = this.bookingDialogService.booking.tenantRelationAssignment?.fromDate?.getTime() === this.value.fromDate.getTime();
            const sameToDate = this.bookingDialogService.booking.tenantRelationAssignment?.toDate?.getTime() === this.value.toDate.getTime();

            if (!sameFromDate || !sameToDate) {
                this._resetTenantRelationCharges();
                this.bookingDialogService.positions.addFromTenantRelation(this.value);
            }

        } else {
            this._resetTenantRelationCharges();
            this.bookingDialogService.subscriptions.addFromTenantRelation(this.value);
        }

        if (!this.value.captureTenantRelation) {
            this.sendUpdate(true);
            return;
        }

        this.bookingDialogService.loading = true;
        this.value.captureTenantRelation = false;
        this.sendUpdate(true);

        setTimeout(() => {
            this.value.captureTenantRelation = true;
            this.sendUpdate(true);
            this.bookingDialogService.loading = false;
        }, 1000);
    }

    getTenantRelationAssignmentWithUpdatedDates(): TenantRelationAssignment {

        const fromDate = this.bookingDialogService.booking.from;
        const toDate = this.bookingDialogService.booking.to;

        return new TenantRelationAssignment({
            ...this.value,
            fromDate: fromDate,
            toDate: toDate,
            periodFromDate: fromDate,
            periodToDate: toDate,
            vestingPeriodFrom: fromDate,
            vestingPeriodUntil: toDate || getVestingPeriodUntil(fromDate, this.value.payableOption),
        } as unknown as TenantRelationAssignmentDto);

    }

}
